import { useState } from 'react'
import toast from 'react-hot-toast'
import { startRegistration } from '@simplewebauthn/browser'
import { api } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'

export default function SettingsPage() {
  const { user } = useAuth()
  const [registering, setRegistering] = useState(false)

  const isReadOnly = user?.role === 'readonly'

  async function handleRegisterPasskey() {
    setRegistering(true)
    try {
      const options = await api.passkeyRegisterOptions()
      const regResponse = await startRegistration(options)
      await api.passkeyRegisterVerify(regResponse)
      toast.success('Face ID set up successfully! 🔑')
    } catch (err) {
      if (err.name === 'InvalidStateError') {
        toast.error('This device already has a passkey registered')
      } else if (err.name === 'NotAllowedError') {
        toast.error('Registration cancelled')
      } else {
        toast.error(err.message || 'Failed to set up Face ID')
      }
    } finally {
      setRegistering(false)
    }
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Settings</h2>

      {/* Account info */}
      <div className="card p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Account</h3>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-semibold">
            {user?.displayName?.[0] || '?'}
          </div>
          <div>
            <p className="font-medium text-gray-900">{user?.displayName}</p>
            <p className="text-sm text-gray-500 capitalize">{user?.role === 'readonly' ? 'Read-only access' : 'Full access'}</p>
          </div>
        </div>
      </div>

      {/* Passkey setup */}
      {!isReadOnly && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-1">Face ID / Touch ID</h3>
          <p className="text-sm text-gray-500 mb-3">
            Set up a passkey on this device so you can sign in with Face ID instead of your password.
          </p>
          <button
            onClick={handleRegisterPasskey}
            disabled={registering}
            className="btn-secondary w-full py-2.5"
          >
            {registering ? 'Setting up…' : '🔑 Set up Face ID on this device'}
          </button>
        </div>
      )}

      {/* App info */}
      <div className="card p-4">
        <h3 className="font-semibold text-gray-900 mb-3">About</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>🍴 <strong>Dinner Planner</strong> — Maksimovich Family</p>
          <p>🌐 <a href="https://dinner.maksimovich.org" className="text-green-600">dinner.maksimovich.org</a></p>
          <p className="text-gray-400 text-xs mt-2">Version 1.0.0</p>
        </div>
      </div>

      {/* Add to home screen tip */}
      <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
        <p className="text-sm text-blue-800 font-medium mb-1">📱 Add to Home Screen</p>
        <p className="text-xs text-blue-600">
          In Safari, tap the Share button then "Add to Home Screen" to use Dinner Planner like an app.
        </p>
      </div>
    </div>
  )
}
